import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { reverseGeocode } from "@/lib/geo";
import { MapPin, NavigationArrow, Clock, WarningCircle, CheckCircle } from "@phosphor-icons/react";

const INTERVAL_MS = 15000;
const distM = (a, b) => { const r = 6371000, t = Math.PI / 180; const dLat = (b.lat - a.lat) * t, dLng = (b.lng - a.lng) * t;
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(a.lat * t) * Math.cos(b.lat * t) * Math.sin(dLng / 2) ** 2; return 2 * r * Math.asin(Math.sqrt(h)); };
const beacon = (token, type) => {
  const url = `${process.env.REACT_APP_BACKEND_URL}/api/track/${token}/event`;
  const body = new Blob([JSON.stringify({ type })], { type: "application/json" });
  if (!navigator.sendBeacon?.(url, body)) api.post(`/track/${token}/event`, { type }).catch(() => {});
};

export default function TrackShare() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [err, setErr] = useState("");
  const [sharing, setSharing] = useState(false);
  const [last, setLast] = useState(null);
  const [geoErr, setGeoErr] = useState("");
  const latest = useRef(null);
  const sentAt = useRef(0);
  const geo = useRef({ at: 0, pos: null, address: "" });

  useEffect(() => {
    document.title = "Location sharing";
    api.get(`/track/${token}`).then(({ data }) => {
      setInfo(data); beacon(token, "opened");
      if (data.accepted) setSharing(true);
    }).catch((e) => setErr(apiError(e.response?.data?.detail)));
  }, [token]);

  useEffect(() => {
    if (!sharing) return undefined;
    const send = async (force) => {
      const p = latest.current;
      if (!p || (!force && Date.now() - sentAt.current < INTERVAL_MS)) return;
      sentAt.current = Date.now();
      const g = geo.current;
      if (!g.pos || distM(g.pos, p) > 60 || Date.now() - g.at > 120000) {
        const address = await reverseGeocode(p.lat, p.lng);
        if (address) geo.current = { at: Date.now(), pos: { lat: p.lat, lng: p.lng }, address };
      }
      try { const { data } = await api.post(`/track/${token}/ping`, { ...p, address: (geo.current.address || "").slice(0, 200) }); setLast({ ...p, address: data.address, at: new Date() }); setGeoErr(""); }
      catch (e) {
        if (e.response?.status === 410) { setSharing(false); setErr(apiError(e.response.data?.detail)); }
        else { sentAt.current = 0; setGeoErr(e.response ? apiError(e.response.data?.detail) : "No internet connection. Retrying…"); }
      }
    };
    const watch = navigator.geolocation.watchPosition(
      (pos) => {
        const c = pos.coords;
        latest.current = { lat: c.latitude, lng: c.longitude, accuracy: c.accuracy || 0, speed: c.speed, heading: c.heading };
        setGeoErr(""); send(false);
      },
      (e) => {
        if (e.code === 1) { beacon(token, "permission_denied"); setGeoErr("Location permission is off. Allow location for this page in your phone settings, then reopen the link."); }
        else setGeoErr("Waiting for GPS signal…");
      },
      { enableHighAccuracy: true, maximumAge: 5000, timeout: 30000 },
    );
    const t = setInterval(() => send(true), INTERVAL_MS);
    send(true);
    let lock;
    const relock = () => { navigator.wakeLock?.request("screen").then((l) => { lock = l; }).catch(() => {}); };
    const onVis = () => { if (document.visibilityState === "visible") { relock(); send(true); } };
    const onHide = () => beacon(token, "paused");
    relock();
    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("pagehide", onHide);
    return () => { navigator.geolocation.clearWatch(watch); clearInterval(t); lock?.release?.(); document.removeEventListener("visibilitychange", onVis); window.removeEventListener("pagehide", onHide); };
  }, [sharing, token]);

  const start = async () => {
    if (!navigator.geolocation) { setGeoErr("This browser can't share location."); return; }
    setGeoErr("Getting your GPS location…");
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const c = pos.coords;
        latest.current = { lat: c.latitude, lng: c.longitude, accuracy: c.accuracy || 0, speed: c.speed, heading: c.heading };
        try { await api.post(`/track/${token}/accept`); setGeoErr(""); setSharing(true); } catch (e) { setErr(apiError(e.response?.data?.detail)); }
      },
      (e) => {
        if (e.code === 1) { beacon(token, "permission_denied"); setGeoErr("Location permission is blocked. Allow location for this website in your phone settings (Settings › Safari/Chrome › Location), then tap the button again."); }
        else setGeoErr("Couldn't get a GPS fix. Make sure Location Services are on, then tap the button again.");
      },
      { enableHighAccuracy: true, timeout: 30000, maximumAge: 0 },
    );
  };

  return (
    <div className="min-h-screen bg-[#05070a] text-white flex items-center justify-center px-5 py-10" data-testid="track-share-page">
      <div className="w-full max-w-md rounded-3xl border border-white/10 bg-white/[0.03] p-7">
        {err ? (
          <div className="text-center py-6" data-testid="track-error"><WarningCircle size={48} weight="duotone" className="mx-auto text-red-400" /><p className="font-heading font-bold text-xl mt-3">{err}</p></div>
        ) : !info ? <p className="text-white/40 text-center py-8">Loading…</p> : !sharing ? (
          <div data-testid="track-consent">
            <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-sky-300">Live Location Request</p>
            <h1 className="font-heading font-extrabold text-3xl mt-3">Hi {info.name},</h1>
            <p className="text-white/65 mt-3">Your dispatcher is asking you to share your truck's live location so they can update the broker and plan your next load.</p>
            <ul className="mt-5 space-y-2 text-sm text-white/60">
              <li className="flex gap-2"><MapPin size={18} className="text-emerald-300 shrink-0" />Accept once. Leave this tab open in the background while driving; you can use other apps and lock your phone.</li>
              <li className="flex gap-2"><Clock size={18} className="text-emerald-300 shrink-0" />Sharing ends automatically {new Date(info.expires_at).toLocaleString()}.</li>
            </ul>
            {geoErr && <p className="mt-4 text-sm text-amber-300">{geoErr}</p>}
            <button onClick={start} className="mt-7 w-full py-3.5 rounded-full bg-gradient-to-r from-sky-500 to-emerald-500 font-semibold text-[15px] hover:brightness-110 transition-[filter]" data-testid="track-accept-btn">Accept & Share My Location</button>
          </div>
        ) : (
          <div className="text-center" data-testid="track-sharing">
            <span className="relative inline-flex"><span className="absolute inset-0 rounded-full bg-emerald-400/40 animate-ping" /><NavigationArrow size={54} weight="fill" className="relative text-emerald-400" /></span>
            <p className="font-heading font-bold text-2xl mt-5">Sharing live location</p>
            <p className="text-sm text-white/55 mt-2">You're all set. Switch to other apps or lock your phone; just don't swipe this browser tab closed. If sharing pauses, you'll get a text to tap the link again.</p>
            {geoErr && <p className="mt-4 text-sm text-amber-300" data-testid="track-geo-error">{geoErr}</p>}
            {!last && !geoErr && <p className="mt-4 text-sm text-white/50" data-testid="track-waiting">Getting your first GPS fix…</p>}
            {last && <div className="mt-6 rounded-2xl border border-white/10 bg-white/[0.03] p-4 text-left text-sm" data-testid="track-last">
              <p className="flex items-center gap-1.5 text-emerald-300 font-semibold"><CheckCircle size={16} />Sent {last.at.toLocaleTimeString()}</p>
              <p className="mt-1 text-white/80">{last.address || `${last.lat.toFixed(5)}, ${last.lng.toFixed(5)}`}</p>
              <p className="text-xs text-white/40 mt-1">Accuracy ±{Math.round(last.accuracy)} m</p></div>}
          </div>
        )}
      </div>
    </div>
  );
}
